import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { shiftService, Shift as ApiShift } from '../services/shiftService';
import { usersService } from '../services/usersService';
import { leaveRequestService } from '../services/leaveRequestService';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import ModernShiftCalendar from './ModernShiftCalendar';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Calendar,
  CalendarDays,
  Clock,
  User,
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Search,
  Filter,
  Download,
  Upload,
  Copy,
  Eye,
  Settings,
} from 'lucide-react';

interface Shift {
  id: string;
  userId: string;
  userName: string;
  date: string;
  startTime: string;
  endTime: string;
  position: string;
  location: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  notes?: string;
  isRecurring: boolean;
  recurringPattern?: 'weekly' | 'biweekly' | 'monthly';
  createdAt: string;
  updatedAt: string;
}

interface StaffMember {
  id: string;
  name: string;
  email: string;
  position: string;
  roles?: string[]; // Store user roles for filtering
  maxHoursPerWeek: number;
  preferredShifts: string[];
  unavailableDates: string[];
  skills: string[];
  isActive: boolean;
}

interface ScheduleConflict {
  id: string;
  type: 'overlap' | 'overtime' | 'unavailable' | 'skill_mismatch';
  shiftId: string;
  staffId: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  suggestedResolution?: string;
}

interface ShiftTemplate {
  id: string;
  name: string;
  position: string;
  startTime: string;
  endTime: string;
  location: string;
  isDefault: boolean;
  notes?: string;
}

// Role hierarchy for filtering users
const ROLE_HIERARCHY: Record<string, number> = {
  sysAdmin: 5,
  admin: 4,
  orgAdmin: 4,
  storeManager: 3,
  staff: 2,
  cashier: 1,
};

// Helper function to get user's highest role level
const getUserHighestRoleLevel = (roles: string[] = []): number => {
  if (!roles || roles.length === 0) return 0;
  return Math.max(...roles.map((role) => ROLE_HIERARCHY[role] || 0));
};

// Helper function to check if a role should be visible to the current user
const shouldShowRole = (userRole: string, viewerRoleLevel: number): boolean => {
  const roleLevel = ROLE_HIERARCHY[userRole] || 0;

  // sysAdmin can see all roles
  if (viewerRoleLevel >= 5) return true;

  // orgAdmin/admin can see all except sysAdmin
  if (viewerRoleLevel >= 4) {
    return roleLevel < 5;
  }

  // storeManager can see all except sysAdmin and orgAdmin/admin
  if (viewerRoleLevel >= 3) {
    return roleLevel < 4;
  }

  // staff can see all except sysAdmin, orgAdmin/admin, and storeManager
  if (viewerRoleLevel >= 2) {
    return roleLevel < 3;
  }

  // cashier can see all except sysAdmin, orgAdmin/admin, storeManager, and staff
  if (viewerRoleLevel >= 1) {
    return roleLevel < 2;
  }

  return false;
};

// Filter staff members based on role hierarchy
const filterStaffByRole = (
  staff: StaffMember[],
  viewerRoles: string[] = []
): StaffMember[] => {
  const viewerRoleLevel = getUserHighestRoleLevel(viewerRoles);

  return staff.filter((member) => {
    // If staff member has no roles, show them (they might not have been assigned roles yet)
    if (!member.roles || member.roles.length === 0) return true;

    // Check if any of the staff member's roles should be visible
    return member.roles.some((role) => shouldShowRole(role, viewerRoleLevel));
  });
};

const ModernShiftPlanning: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();

  const [shifts, setShifts] = useState<Shift[]>([]);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [conflicts, setConflicts] = useState<ScheduleConflict[]>([]);
  const [templates, setTemplates] = useState<ShiftTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [viewMode, setViewMode] = useState<
    'week' | 'month' | 'list' | 'calendar'
  >('calendar');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [staffSearchOpen, setStaffSearchOpen] = useState(false);
  const [staffSearchValue, setStaffSearchValue] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Form state for creating/editing shifts
  const [shiftForm, setShiftForm] = useState({
    userId: '',
    date: '',
    startTime: '',
    endTime: '',
    position: '',
    location: '',
    notes: '',
    isRecurring: false,
    recurringPattern: 'weekly' as 'weekly' | 'biweekly' | 'monthly',
  });

  useEffect(() => {
    if (user) {
      loadShifts();
      loadStaffMembers();
      detectConflicts(); // Re-run conflict detection when user loads/shifts reload
    }
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Reload shifts when view mode or selected date changes
  useEffect(() => {
    loadShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedDate]);

  useEffect(() => {
    if (shifts.length > 0) {
      detectConflicts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts]);

  // Transform API shift to component shift format
  const transformShift = (apiShift: ApiShift): Shift => {
    const startTime = new Date(apiShift.startTime);
    const endTime = new Date(apiShift.endTime);

    return {
      id: apiShift.id,
      userId: apiShift.staffId,
      userName: apiShift.staffName,
      date: format(startTime, 'yyyy-MM-dd'),
      startTime: format(startTime, 'HH:mm'),
      endTime: format(endTime, 'HH:mm'),
      position: apiShift.position || 'Staff',
      location: 'Main Store', // TODO: Get from shift data when available
      status: apiShift.status,
      notes: apiShift.description,
      isRecurring: apiShift.isRecurring,
      recurringPattern:
        apiShift.recurringPattern?.frequency === 'weekly'
          ? 'weekly'
          : apiShift.recurringPattern?.frequency === 'monthly'
          ? 'monthly'
          : undefined,
      createdAt: apiShift.createdAt || new Date().toISOString(),
      updatedAt: apiShift.updatedAt || new Date().toISOString(),
    };
  };

  const loadShifts = async () => {
    setLoading(true);
    try {
      // Calculate date range based on current view
      // For calendar view, show 3 months (1 month before, current month, 1 month after)
      // For list view, show 2 months from today
      const today = new Date();
      let startDate: Date;
      let endDate: Date;

      if (viewMode === 'calendar') {
        // Show 3 months: 1 month before selected date, current month, 1 month after
        const selectedDateObj = selectedDate ? new Date(selectedDate) : today;
        startDate = new Date(
          selectedDateObj.getFullYear(),
          selectedDateObj.getMonth() - 1,
          1
        );
        endDate = new Date(
          selectedDateObj.getFullYear(),
          selectedDateObj.getMonth() + 2,
          0
        );
        endDate.setHours(23, 59, 59, 999); // End of last day
      } else {
        // For list view, show 2 months from today
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        endDate.setHours(23, 59, 59, 999);
      }

      console.log('📅 Loading shifts for date range:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        viewMode,
        selectedDate,
      });

      const allShifts = await shiftService.getShiftsByDateRange(
        startDate,
        endDate
      );
      console.log('📋 Loaded shifts from API:', allShifts.length, 'shifts');
      console.log(
        '📋 Shift IDs:',
        allShifts.map((s) => s.id)
      );

      const transformedShifts = allShifts.map((shift, index) => {
        try {
          return transformShift(shift);
        } catch (error) {
          console.error(
            `❌ Error transforming shift at index ${index}:`,
            error,
            shift
          );
          // Return a fallback shift to prevent breaking the list
          return {
            id: shift.id || `error-${index}`,
            userId: shift.staffId || '',
            userName: shift.staffName || 'Unknown',
            date: new Date().toISOString().split('T')[0],
            startTime: '00:00',
            endTime: '00:00',
            position: shift.position || 'Staff',
            location: 'Main Store',
            status: shift.status || 'scheduled',
            notes: shift.description,
            isRecurring: shift.isRecurring || false,
            recurringPattern: undefined,
            createdAt: shift.createdAt || new Date().toISOString(),
            updatedAt: shift.updatedAt || new Date().toISOString(),
          };
        }
      });

      console.log('✅ Transformed shifts:', transformedShifts.length);
      setShifts(transformedShifts);
    } catch (error) {
      console.error('Error loading shifts:', error);
      notify('Failed to load shifts', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadStaffMembers = async () => {
    try {
      // Fetch all users/staff members from the API
      const users = await usersService.getUsers({
        page: 1,
        size: 1000, // Get all staff members
      });

      const usersArray = Array.isArray(users) ? users : [];

      // Fetch approved leave requests to mark unavailable dates
      const leaveRequests = await leaveRequestService.getLeaveRequests();
      const approvedLeave = leaveRequests.filter(req => req.status === 'approved');

      // Map users to StaffMember format
      const staffMembersList: StaffMember[] = usersArray.map((user) => {
        // Construct name from firstName and lastName, with fallbacks
        const name =
          (user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.firstName || user.lastName) ||
          user.username ||
          user.email ||
          'Unknown';

        // Get unavailable dates from approved leave requests
        const staffLeave = approvedLeave.filter(req => req.staffId === user.id);
        const unavailableDates: string[] = [];
        
        staffLeave.forEach(leave => {
          try {
            const start = parseISO(typeof leave.startDate === 'string' ? leave.startDate : (leave.startDate as any).toISOString());
            const end = parseISO(typeof leave.endDate === 'string' ? leave.endDate : (leave.endDate as any).toISOString());
            
            const days = eachDayOfInterval({ start, end });
            days.forEach(day => {
              unavailableDates.push(format(day, 'yyyy-MM-dd'));
            });
          } catch (e) {
            console.error('Error parsing leave dates:', e);
          }
        });

        return {
          id: user.id,
          name,
          email: user.email || '',
          position: (user as any).position || (user as any).role || 'Staff',
          roles: user.roles || [], // Preserve user roles for filtering
          maxHoursPerWeek: 40,
          preferredShifts: [],
          unavailableDates,
          skills: [],
          isActive: (user as any).isActive !== false, // Default to true if not specified
        };
      });

      setStaffMembers(staffMembersList);
    } catch (error) {
      console.error('Error loading staff members:', error);
      notify('Failed to load staff members', { type: 'error' });
      // Fallback: try to extract from shifts if API fails
      try {
        const allShifts = await shiftService.getShifts();
        const staffMap = new Map<string, StaffMember>();

        allShifts.forEach((shift) => {
          if (!staffMap.has(shift.staffId)) {
            staffMap.set(shift.staffId, {
              id: shift.staffId,
              name: shift.staffName,
              email: '',
              position: shift.position || 'Staff',
              roles: [], // No role info available from shifts fallback
              maxHoursPerWeek: 40,
              preferredShifts: [],
              unavailableDates: [],
              skills: [],
              isActive: true,
            });
          }
        });

        setStaffMembers(Array.from(staffMap.values()));
      } catch (fallbackError) {
        console.error(
          'Error loading staff from shifts fallback:',
          fallbackError
        );
      }
    }
  };

  // Filter staff members by role
  const roleFilteredStaffMembers = useMemo(() => {
    return filterStaffByRole(staffMembers, user?.roles || []);
  }, [staffMembers, user?.roles]);

  // Filter staff members by search
  const filteredStaffMembers = useMemo(() => {
    if (!staffSearchValue.trim()) {
      return roleFilteredStaffMembers;
    }

    const searchLower = staffSearchValue.toLowerCase();
    return roleFilteredStaffMembers.filter(
      (staff) =>
        staff.name.toLowerCase().includes(searchLower) ||
        staff.email.toLowerCase().includes(searchLower) ||
        staff.position.toLowerCase().includes(searchLower)
    );
  }, [roleFilteredStaffMembers, staffSearchValue]);

  // Reset search value when dialog closes
  useEffect(() => {
    if (!showCreateDialog) {
      setStaffSearchValue('');
      setStaffSearchOpen(false);
    } else {
      // Initialize date field with selectedDate when dialog opens
      setShiftForm((prev) => ({
        ...prev,
        date: prev.date || selectedDate,
      }));
    }
  }, [showCreateDialog, selectedDate]);

  const loadTemplates = async () => {
    try {
      // Mock templates
      const mockTemplates: ShiftTemplate[] = [
        {
          id: '1',
          name: 'Morning Barista',
          position: 'Barista',
          startTime: '08:00',
          endTime: '16:00',
          location: 'Main Store',
          isDefault: true,
          notes: 'Standard morning shift',
        },
        {
          id: '2',
          name: 'Afternoon Cashier',
          position: 'Cashier',
          startTime: '12:00',
          endTime: '20:00',
          location: 'Main Store',
          isDefault: true,
          notes: 'Standard afternoon shift',
        },
        {
          id: '3',
          name: 'Evening Barista',
          position: 'Barista',
          startTime: '16:00',
          endTime: '24:00',
          location: 'Main Store',
          isDefault: false,
          notes: 'Evening shift for closing',
        },
      ];

      setTemplates(mockTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const detectConflicts = () => {
    const detectedConflicts: ScheduleConflict[] = [];
    const conflictKeys = new Set<string>(); // Track unique conflicts by staffId-shiftId pair

    // Check for overlapping shifts for the same staff member
    shifts.forEach((shift, index) => {
      // Skip if shift has no assigned staff (unassigned shifts can't conflict)
      if (!shift.userId || shift.userId.trim() === '') {
        return;
      }

      const shiftStart = new Date(`${shift.date}T${shift.startTime}`);
      const shiftEnd = new Date(`${shift.date}T${shift.endTime}`);

      shifts.slice(index + 1).forEach((otherShift) => {
        // Only check conflicts for the same staff member
        // Skip if other shift has no assigned staff
        if (
          shift.userId === otherShift.userId &&
          shift.userId &&
          shift.userId.trim() !== '' &&
          otherShift.userId &&
          otherShift.userId.trim() !== ''
        ) {
          const otherStart = new Date(
            `${otherShift.date}T${otherShift.startTime}`
          );
          const otherEnd = new Date(`${otherShift.date}T${otherShift.endTime}`);

          // Check for overlap
          if (
            (shiftStart >= otherStart && shiftStart < otherEnd) ||
            (shiftEnd > otherStart && shiftEnd <= otherEnd) ||
            (shiftStart <= otherStart && shiftEnd >= otherEnd)
          ) {
            // Create unique conflict key using staffId and both shift IDs (sorted to avoid duplicates)
            const shiftIds = [shift.id, otherShift.id].sort();
            const conflictKey = `${shift.userId}-${shiftIds[0]}-${shiftIds[1]}`;

            // Only add conflict if we haven't seen this pair before
            if (!conflictKeys.has(conflictKey)) {
              conflictKeys.add(conflictKey);

              // Add conflict for the first shift
              detectedConflicts.push({
                id: `conflict-${shift.userId}-${shift.id}`,
                type: 'overlap',
                shiftId: shift.id,
                staffId: shift.userId,
                description: `Staff member has overlapping shifts on ${shift.date}`,
                severity: 'high',
                suggestedResolution:
                  'Adjust start or end time for one of the shifts',
              });

              // Add conflict for the second shift
              detectedConflicts.push({
                id: `conflict-${shift.userId}-${otherShift.id}`,
                type: 'overlap',
                shiftId: otherShift.id,
                staffId: shift.userId,
                description: `Staff member has overlapping shifts on ${otherShift.date}`,
                severity: 'high',
                suggestedResolution:
                  'Adjust start or end time for one of the shifts',
              });
            }
          }
        }
      });
    });

    setConflicts(detectedConflicts);
  };

  const filteredShifts = shifts.filter((shift) => {
    const matchesSearch =
      shift.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.location.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPosition =
      filterPosition === 'all' || shift.position === filterPosition;
    const matchesStatus =
      filterStatus === 'all' || shift.status === filterStatus;

    return matchesSearch && matchesPosition && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-gray-500" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getConflictIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleCreateShift = async () => {
    if (!shiftForm.date || !shiftForm.startTime || !shiftForm.endTime) {
      notify(
        translate(
          'pos.staff.common.validation_required',
          'Please fill in all required fields (date, start time, end time)'
        ),
        { type: 'error' }
      );
      return;
    }

    try {
      const selectedStaff = shiftForm.userId
        ? staffMembers.find((s) => s.id === shiftForm.userId)
        : null;

      // Format date-time strings with timezone to preserve the selected date exactly
      const formatDateTimeWithTimezone = (
        dateStr: string,
        timeStr: string
      ): string => {
        const [hours, minutes] = timeStr.split(':');
        // Get timezone offset in minutes
        const now = new Date();
        const offsetMinutes = now.getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        const offsetMins = Math.abs(offsetMinutes) % 60;
        const offsetSign = offsetMinutes <= 0 ? '+' : '-';
        const offsetStr = `${offsetSign}${String(offsetHours).padStart(
          2,
          '0'
        )}:${String(offsetMins).padStart(2, '0')}`;

        // Construct ISO string with timezone: YYYY-MM-DDTHH:mm:ss+HH:mm
        return `${dateStr}T${hours}:${minutes}:00${offsetStr}`;
      };

      // Get tenantId from the first existing shift or use a placeholder
      // The API will automatically set the correct tenantId based on the authenticated user
      const existingShifts = await shiftService.getShifts();
      const tenantIdFromExisting =
        existingShifts.length > 0 ? existingShifts[0].tenantId : undefined;

      console.log('🔍 Creating shift with:', {
        date: shiftForm.date,
        startTime: shiftForm.startTime,
        endTime: shiftForm.endTime,
        userId: shiftForm.userId,
        staffName: selectedStaff?.name,
        userTenantId: (user as any)?.tenantId,
        existingTenantId: tenantIdFromExisting,
      });

      // Call the API to create the shift
      // Note: The API should automatically set tenantId from the authenticated user's context
      const createdShift = await shiftService.createShift({
        title: shiftForm.position || 'Shift',
        startTime: formatDateTimeWithTimezone(
          shiftForm.date,
          shiftForm.startTime
        ),
        endTime: formatDateTimeWithTimezone(shiftForm.date, shiftForm.endTime),
        staffId: shiftForm.userId || '',
        staffName: selectedStaff?.name || '',
        position: shiftForm.position || undefined,
        status: 'scheduled',
        description: shiftForm.notes,
        isRecurring: shiftForm.isRecurring,
        recurringPattern: shiftForm.isRecurring
          ? {
              frequency:
                shiftForm.recurringPattern === 'weekly'
                  ? 'weekly'
                  : shiftForm.recurringPattern === 'monthly'
                  ? 'monthly'
                  : 'weekly',
              interval: 1,
            }
          : undefined,
        createdBy: String(user?.id || ''),
        // Use tenantId from existing shifts if available, otherwise let API handle it
        tenantId: tenantIdFromExisting || (user as any)?.tenantId || '',
      });

      console.log('✅ Shift created successfully:', createdShift);
      console.log('✅ Created shift ID:', createdShift.id);
      console.log('✅ Created shift tenantId:', createdShift.tenantId);

      // Add the newly created shift to the list immediately (optimistic update)
      if (createdShift) {
        const transformedNewShift = transformShift(createdShift);
        setShifts((prev) => [transformedNewShift, ...prev]);
      }

      // Reload shifts to get the updated list from the server (ensures consistency)
      try {
        await loadShifts();
      } catch (reloadError) {
        console.warn(
          'Failed to reload shifts, but shift was created:',
          reloadError
        );
        // Don't show error to user since the shift was already added optimistically
      }

      setShowCreateDialog(false);
      setShiftForm({
        userId: '',
        date: '',
        startTime: '',
        endTime: '',
        position: '',
        location: '',
        notes: '',
        isRecurring: false,
        recurringPattern: 'weekly',
      });

      notify(
        translate(
          'pos.staff.shift_planning.create_success',
          'Shift created successfully!'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error creating shift:', error);
      notify(
        translate(
          'pos.staff.shift_planning.create_error',
          'Failed to create shift'
        ),
        { type: 'error' }
      );
    }
  };

  const handleEditShift = (shift: Shift) => {
    setEditingShift(shift);
    setShiftForm({
      userId: shift.userId,
      date: shift.date,
      startTime: shift.startTime,
      endTime: shift.endTime,
      position: shift.position,
      location: shift.location,
      notes: shift.notes || '',
      isRecurring: shift.isRecurring,
      recurringPattern: shift.recurringPattern || 'weekly',
    });
    setShowEditDialog(true);
  };

  const handleUpdateShift = async () => {
    if (!editingShift) return;

    try {
      const selectedStaff = shiftForm.userId
        ? staffMembers.find((s) => s.id === shiftForm.userId)
        : null;
      const updatedShift: Shift = {
        ...editingShift,
        userId: shiftForm.userId || '',
        userName: selectedStaff?.name || '',
        date: shiftForm.date,
        startTime: shiftForm.startTime,
        endTime: shiftForm.endTime,
        position: shiftForm.position || '',
        location: shiftForm.location,
        notes: shiftForm.notes,
        isRecurring: shiftForm.isRecurring,
        recurringPattern: shiftForm.recurringPattern,
        updatedAt: new Date().toISOString(),
      };

      setShifts((prev) =>
        prev.map((s) => (s.id === editingShift.id ? updatedShift : s))
      );
      setShowEditDialog(false);
      setEditingShift(null);

      notify(
        translate(
          'pos.staff.shift_planning.update_success',
          'Shift updated successfully!'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error updating shift:', error);
      notify(
        translate(
          'pos.staff.shift_planning.update_error',
          'Failed to update shift'
        ),
        { type: 'error' }
      );
    }
  };

  const handleDeleteShift = async (shiftId: string) => {
    try {
      setShifts((prev) => prev.filter((s) => s.id !== shiftId));
      notify(
        translate(
          'pos.staff.shift_planning.delete_success',
          'Shift deleted successfully!'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error deleting shift:', error);
      notify(
        translate(
          'pos.staff.shift_planning.delete_error',
          'Failed to delete shift'
        ),
        { type: 'error' }
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const exportToCSV = () => {
    try {
      const headers = [
        'Staff Member',
        'Date',
        'Start Time',
        'End Time',
        'Position',
        'Location',
        'Status',
        'Notes',
      ];
      const csvContent = [
        headers.join(','),
        ...filteredShifts.map((shift) =>
          [
            shift.userName,
            formatDate(shift.date),
            shift.startTime,
            shift.endTime,
            shift.position,
            shift.location,
            shift.status,
            shift.notes || '',
          ].join(',')
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shifts_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      notify('Shifts exported successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error exporting shifts:', error);
      notify('Failed to export shifts', { type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {translate(
              'pos.staff.shift_planning.loading',
              'Loading shift planning...'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.staff.tabs.shift_planning', 'Shift Planning')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.staff.tabs.shift_planning_desc',
              'Manage staff schedules, assignments, and resolve conflicts'
            )}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {translate(
              'pos.staff.shift_planning.create_button',
              'Create Shift'
            )}
          </Button>
          <Button onClick={exportToCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            {translate('pos.staff.common.export', 'Export')}
          </Button>
        </div>
      </div>

      {/* Shifts Table or Calendar View */}
      {viewMode === 'calendar' ? (
        <ModernShiftCalendar />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {translate(
                'pos.staff.shift_planning.scheduled_shifts_title',
                'Scheduled Shifts'
              )}
            </CardTitle>
            <CardDescription>
              {translate(
                'pos.staff.shift_planning.scheduled_shifts_description',
                'Manage and monitor all staff shifts'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {translate(
                      'pos.staff.shift_planning.table_staff',
                      'Staff Member'
                    )}
                  </TableHead>
                  <TableHead>
                    {translate(
                      'pos.staff.shift_planning.table_datetime',
                      'Date & Time'
                    )}
                  </TableHead>
                  <TableHead>
                    {translate(
                      'pos.staff.shift_planning.table_position',
                      'Position'
                    )}
                  </TableHead>
                  <TableHead>
                    {translate(
                      'pos.staff.shift_planning.table_location',
                      'Location'
                    )}
                  </TableHead>
                  <TableHead>
                    {translate(
                      'pos.staff.shift_planning.table_status',
                      'Status'
                    )}
                  </TableHead>
                  <TableHead>
                    {translate(
                      'pos.staff.shift_planning.table_actions',
                      'Actions'
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredShifts.map((shift) => {
                  // Check if this shift has a conflict
                  const hasConflict = conflicts.some(
                    (conflict) => conflict.shiftId === shift.id
                  );
                  const isMyShift = shift.userId === user?.id;
                  
                  return (
                    <TableRow
                      key={shift.id}
                      className={
                        hasConflict
                          ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500'
                          : isMyShift
                          ? 'bg-green-50 hover:bg-green-100 border-l-4 border-l-green-500'
                          : ''
                      }
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium flex items-center gap-2">
                            <span data-sensitive-field="userId">
                              {shift.userName}
                            </span>
                            {isMyShift && (
                              <Badge className="bg-green-600 hover:bg-green-700 h-5 text-[10px] px-1.5">You</Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <span data-sensitive-field="userId">
                              {shift.userId}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {formatDate(shift.date)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatTime(shift.startTime)} -{' '}
                            {formatTime(shift.endTime)}
                          </div>
                          {shift.isRecurring && (
                            <Badge variant="outline" className="text-xs">
                              {shift.recurringPattern}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{shift.position}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{shift.location}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {hasConflict && (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                          {getStatusIcon(shift.status)}
                          <Badge className={getStatusColor(shift.status)}>
                            {shift.status}
                          </Badge>
                          {hasConflict && (
                            <Badge variant="destructive" className="text-xs">
                              Conflict
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditShift(shift)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteShift(shift.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create Shift Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          className="max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {translate(
                'pos.staff.shift_planning.create_title',
                'Create New Shift'
              )}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.staff.shift_planning.create_description',
                'Create a new shift. Staff selection is optional - unassigned shifts will be available for staff to pick up.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="staff">
                  {translate(
                    'pos.staff.shift_planning.staff_label',
                    'Staff Member (Optional)'
                  )}
                </Label>
                <Popover
                  open={staffSearchOpen}
                  onOpenChange={setStaffSearchOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={staffSearchOpen}
                      className="w-full justify-between cursor-pointer"
                      type="button"
                      onClick={() => setStaffSearchOpen(true)}
                    >
                      {shiftForm.userId
                        ? (() => {
                            const selectedStaff = roleFilteredStaffMembers.find(
                              (staff) => staff.id === shiftForm.userId
                            );
                            return selectedStaff ? (
                              <span data-sensitive-field="userId">
                                {selectedStaff.name}
                              </span>
                            ) : (
                              translate(
                                'pos.staff.shift_planning.staff_placeholder',
                                'Select staff member (optional)...'
                              )
                            );
                          })()
                        : translate(
                            'pos.staff.shift_planning.staff_placeholder',
                            'Select staff member (optional)...'
                          )}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50 pointer-events-none" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder={translate(
                          'pos.staff.shift_planning.search_staff',
                          'Search staff members...'
                        )}
                        value={staffSearchValue}
                        onValueChange={setStaffSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {translate(
                            'pos.staff.shift_planning.no_staff_found',
                            'No staff members found.'
                          )}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              setShiftForm((prev) => ({
                                ...prev,
                                userId: '',
                                position: '',
                              }));
                              setStaffSearchOpen(false);
                              setStaffSearchValue('');
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              setShiftForm((prev) => ({
                                ...prev,
                                userId: '',
                                position: '',
                              }));
                              setStaffSearchOpen(false);
                              setStaffSearchValue('');
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium text-muted-foreground">
                                {translate(
                                  'pos.staff.shift_planning.no_staff',
                                  'No staff (Available for pickup)'
                                )}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {translate(
                                  'pos.staff.shift_planning.no_staff_description',
                                  'Leave unassigned for staff to claim'
                                )}
                              </span>
                            </div>
                          </CommandItem>
                          {filteredStaffMembers.map((staff) => (
                            <CommandItem
                              key={staff.id}
                              value={`${staff.name} ${staff.email} ${staff.position}`}
                              onSelect={() => {
                                setShiftForm((prev) => ({
                                  ...prev,
                                  userId: staff.id,
                                  position: staff.position,
                                }));
                                setStaffSearchOpen(false);
                                setStaffSearchValue('');
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                setShiftForm((prev) => ({
                                  ...prev,
                                  userId: staff.id,
                                  position: staff.position,
                                }));
                                setStaffSearchOpen(false);
                                setStaffSearchValue('');
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  <span data-sensitive-field="userId">
                                    {staff.name}
                                  </span>
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  <span data-sensitive-field="userEmail">
                                    {staff.email}
                                  </span>{' '}
                                  • {staff.position}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label htmlFor="date">
                  {translate('pos.staff.shift_planning.date_label', 'Date *')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="date"
                    type="date"
                    value={shiftForm.date}
                    onChange={(e) =>
                      setShiftForm((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className="flex-1"
                  />
                  <Popover
                    open={datePickerOpen}
                    onOpenChange={setDatePickerOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="px-3">
                        <Calendar className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="p-3">
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
                            <div
                              key={day}
                              className="h-8 w-8 flex items-center justify-center text-xs font-medium text-muted-foreground"
                            >
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {(() => {
                            const currentDate = shiftForm.date
                              ? new Date(shiftForm.date)
                              : new Date();
                            const firstDayOfMonth = new Date(
                              currentDate.getFullYear(),
                              currentDate.getMonth(),
                              1
                            );
                            const lastDayOfMonth = new Date(
                              currentDate.getFullYear(),
                              currentDate.getMonth() + 1,
                              0
                            );
                            const startDate = new Date(firstDayOfMonth);
                            startDate.setDate(
                              startDate.getDate() - firstDayOfMonth.getDay()
                            );

                            const days = [];
                            for (let i = 0; i < 42; i++) {
                              const date = new Date(startDate);
                              date.setDate(startDate.getDate() + i);
                              const isCurrentMonth =
                                date.getMonth() === currentDate.getMonth();
                              const isToday =
                                date.toDateString() ===
                                new Date().toDateString();
                              const isSelected =
                                shiftForm.date &&
                                date.toDateString() ===
                                  new Date(shiftForm.date).toDateString();
                              const dateStr = format(date, 'yyyy-MM-dd');

                              days.push(
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => {
                                    setShiftForm((prev) => ({
                                      ...prev,
                                      date: dateStr,
                                    }));
                                    setDatePickerOpen(false);
                                  }}
                                  className={`h-8 w-8 text-sm rounded-md hover:bg-accent hover:text-accent-foreground ${
                                    isCurrentMonth
                                      ? 'text-foreground'
                                      : 'text-muted-foreground'
                                  } ${
                                    isToday ? 'bg-accent font-semibold' : ''
                                  } ${
                                    isSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : ''
                                  }`}
                                >
                                  {date.getDate()}
                                </button>
                              );
                            }
                            return days;
                          })()}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="startTime">
                  {translate(
                    'pos.staff.shift_planning.start_time_label',
                    'Start Time *'
                  )}
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="endTime">
                  {translate(
                    'pos.staff.shift_planning.end_time_label',
                    'End Time *'
                  )}
                </Label>
                <Input
                  id="endTime"
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="position">
                  {translate(
                    'pos.staff.shift_planning.position_label',
                    'Position (Optional)'
                  )}
                </Label>
                <Select
                  value={shiftForm.position}
                  onValueChange={(value) =>
                    setShiftForm((prev) => ({ ...prev, position: value }))
                  }
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue
                      placeholder={translate(
                        'pos.staff.shift_planning.position_placeholder',
                        'Select position'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Barista">Barista</SelectItem>
                    <SelectItem value="Cashier">Cashier</SelectItem>
                    <SelectItem value="Waiter">Waiter</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="location">
                  {translate(
                    'pos.staff.shift_planning.location_label',
                    'Location (Optional)'
                  )}
                </Label>
                <Input
                  id="location"
                  value={shiftForm.location}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    'pos.staff.shift_planning.location_placeholder',
                    'Enter location'
                  )}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">
                {translate('pos.staff.shift_planning.notes_label', 'Notes')}
              </Label>
              <Textarea
                id="notes"
                value={shiftForm.notes}
                onChange={(e) =>
                  setShiftForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={translate(
                  'pos.staff.shift_planning.notes_placeholder',
                  'Additional notes for this shift...'
                )}
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={shiftForm.isRecurring}
                onChange={(e) =>
                  setShiftForm((prev) => ({
                    ...prev,
                    isRecurring: e.target.checked,
                  }))
                }
              />
              <Label htmlFor="isRecurring">
                {translate(
                  'pos.staff.shift_planning.recurring_label',
                  'Make this a recurring shift'
                )}
              </Label>
            </div>

            {shiftForm.isRecurring && (
              <div>
                <Label htmlFor="recurringPattern">
                  {translate(
                    'pos.staff.shift_planning.recurring_pattern_label',
                    'Recurring Pattern'
                  )}
                </Label>
                <Select
                  value={shiftForm.recurringPattern}
                  onValueChange={(value) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      recurringPattern: value as
                        | 'weekly'
                        | 'biweekly'
                        | 'monthly',
                    }))
                  }
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue
                      placeholder={translate(
                        'pos.staff.shift_planning.recurring_pattern_placeholder',
                        'Select pattern'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">
                      {translate(
                        'pos.staff.shift_planning.recurring_weekly',
                        'Weekly'
                      )}
                    </SelectItem>
                    <SelectItem value="biweekly">
                      {translate(
                        'pos.staff.shift_planning.recurring_biweekly',
                        'Bi-weekly'
                      )}
                    </SelectItem>
                    <SelectItem value="monthly">
                      {translate(
                        'pos.staff.shift_planning.recurring_monthly',
                        'Monthly'
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateShift();
                }}
                className="flex-1"
              >
                {translate(
                  'pos.staff.shift_planning.create_button',
                  'Create Shift'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1"
              >
                {translate('pos.common.action.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          className="max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {translate('pos.staff.shift_planning.edit_title', 'Edit Shift')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.staff.shift_planning.edit_description',
                'Update shift details'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-staff">
                  {translate(
                    'pos.staff.shift_planning.staff_label',
                    'Staff Member (Optional)'
                  )}
                </Label>
                <Select
                  value={shiftForm.userId}
                  onValueChange={(value) =>
                    setShiftForm((prev) => ({ ...prev, userId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={translate(
                        'pos.staff.shift_planning.staff_placeholder',
                        'Select staff member (optional)...'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">
                      {translate(
                        'pos.staff.shift_planning.no_staff',
                        'No staff (Available for pickup)'
                      )}
                    </SelectItem>
                    {staffMembers.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        <span data-sensitive-field="userId">{staff.name}</span>{' '}
                        ({staff.position})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-date">
                  {translate('pos.staff.shift_planning.date_label', 'Date *')}
                </Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={shiftForm.date}
                  onChange={(e) =>
                    setShiftForm((prev) => ({ ...prev, date: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-startTime">
                  {translate(
                    'pos.staff.shift_planning.start_time_label',
                    'Start Time *'
                  )}
                </Label>
                <Input
                  id="edit-startTime"
                  type="time"
                  value={shiftForm.startTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      startTime: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-endTime">
                  {translate(
                    'pos.staff.shift_planning.end_time_label',
                    'End Time *'
                  )}
                </Label>
                <Input
                  id="edit-endTime"
                  type="time"
                  value={shiftForm.endTime}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      endTime: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-position">
                  {translate(
                    'pos.staff.shift_planning.position_label',
                    'Position *'
                  )}
                </Label>
                <Select
                  value={shiftForm.position}
                  onValueChange={(value) =>
                    setShiftForm((prev) => ({ ...prev, position: value }))
                  }
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue
                      placeholder={translate(
                        'pos.staff.shift_planning.position_placeholder',
                        'Select position'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Barista">Barista</SelectItem>
                    <SelectItem value="Cashier">Cashier</SelectItem>
                    <SelectItem value="Waiter">Waiter</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-location">
                  {translate(
                    'pos.staff.shift_planning.location_label',
                    'Location *'
                  )}
                </Label>
                <Input
                  id="edit-location"
                  value={shiftForm.location}
                  onChange={(e) =>
                    setShiftForm((prev) => ({
                      ...prev,
                      location: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    'pos.staff.shift_planning.location_placeholder',
                    'Enter location'
                  )}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-notes">
                {translate('pos.staff.shift_planning.notes_label', 'Notes')}
              </Label>
              <Textarea
                id="edit-notes"
                value={shiftForm.notes}
                onChange={(e) =>
                  setShiftForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder={translate(
                  'pos.staff.shift_planning.notes_placeholder',
                  'Additional notes for this shift...'
                )}
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={handleUpdateShift}
                className="flex-1"
              >
                {translate(
                  'pos.staff.shift_planning.update_button',
                  'Update Shift'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                {translate('pos.common.action.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernShiftPlanning;
